import { PayoutEarningLine, WithdrawalRequest } from "../../../common/requests/transactions/endpoints";
import { isBroughtBuyer } from "../../../common/utils/bringBuyers";
import { formatMoney } from "../../../common/utils/format";

/** wa.me link for a phone number in any format. */
export const whatsappUrl = (phone: string) => `https://wa.me/${phone.replace(/[^\d]/g, "")}`;

export const isPaid = (r: WithdrawalRequest) => r.status === "Paid";

/** "S$420.50 + Rp3,400,000" for a set of requests. */
export function totalsByCurrency(rows: { amount: number; currency: string }[]): string {
  const sums = new Map<string, number>();
  rows.forEach((r) => sums.set(r.currency, (sums.get(r.currency) ?? 0) + r.amount));
  return Array.from(sums, ([currency, amount]) => formatMoney(amount, currency)).join(" + ") || "–";
}

export interface PayoutLineTotals {
  count: number;
  sale: number;
  fee: number;
  vouchers: number;
  voucherCount: number;
  /** Orders from the seller's own buyers (0% or part 0% fee) */
  brought: number;
  net: number;
}

const cents = (n: number) => Math.round(n * 100) / 100;

/** Sums a payout's orders. sale − fee − vouchers = net, and net equals the
 * request amount (the server only sends lines that add up to it). */
export function payoutLineTotals(lines: PayoutEarningLine[]): PayoutLineTotals {
  const t = lines.reduce(
    (acc, l) => ({
      count: acc.count + 1,
      sale: acc.sale + l.sale,
      fee: acc.fee + l.fee,
      vouchers: acc.vouchers + (l.voucher_share || 0),
      voucherCount: acc.voucherCount + (l.voucher_share > 0 ? 1 : 0),
      brought: acc.brought + (isBroughtBuyer(l.fee_reason) ? 1 : 0),
      net: acc.net + l.amount,
    }),
    { count: 0, sale: 0, fee: 0, vouchers: 0, voucherCount: 0, brought: 0, net: 0 }
  );
  return { ...t, sale: cents(t.sale), fee: cents(t.fee), vouchers: cents(t.vouchers), net: cents(t.net) };
}

/** Newest first, so the list reads like the seller's order history. */
export const sortPayoutLines = (lines: PayoutEarningLine[]) =>
  lines.slice().sort((a, b) => (b.created_at ?? "").localeCompare(a.created_at ?? ""));

const csvCell = (value: unknown) => {
  if (value === null || value === undefined) return "";
  const s = String(value);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function payoutsToCsv(rows: WithdrawalRequest[]): string {
  const header = [
    "withdrawal_id",
    "requested_at",
    "seller",
    "email",
    "amount",
    "currency",
    "currency_mixed",
    "status",
    "paid_at",
    "paid_by",
    "payout_reference",
    "note",
  ];
  const lines = rows.map((r) =>
    [
      r.withdrawal_id,
      r.request_date,
      r.username,
      r.email,
      r.amount,
      r.currency,
      r.currency_mixed,
      r.status,
      r.payout?.paid_at,
      r.payout?.paid_by_email,
      r.payout?.payout_reference,
      r.payout?.note,
    ]
      .map(csvCell)
      .join(",")
  );
  return [header.join(","), ...lines].join("\n");
}

export type PayoutView = "pending" | "paid" | "all";

export const parseView = (value: string | null): PayoutView =>
  value === "paid" || value === "all" ? value : "pending";

/** The request a `?payout=<id>` param points at (ids compared trimmed). */
export function findPayout<T extends { withdrawal_id: string }>(rows: T[], id: string | null): T | null {
  const want = (id ?? "").trim();
  if (!want) return null;
  return rows.find((r) => (r.withdrawal_id ?? "").trim() === want) ?? null;
}

/** Requests in a tab, matching a search on seller, email or payout reference.
 * The queue is oldest-first (pay in order); history is newest-first. */
export function filterPayouts(rows: WithdrawalRequest[], view: PayoutView, query: string): WithdrawalRequest[] {
  const q = query.trim().toLowerCase();
  return rows
    .filter((r) => (view === "pending" ? !isPaid(r) : view === "paid" ? isPaid(r) : true))
    .filter((r) => !q || `${r.username ?? ""} ${r.email ?? ""} ${r.payout?.payout_reference ?? ""}`.toLowerCase().includes(q))
    .sort((a, b) => (view === "pending" ? 1 : -1) * (new Date(a.request_date).getTime() - new Date(b.request_date).getTime()));
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "3 Oct" (adds the year when it isn't this year): the short date on a Paid pill. */
export function shortDate(value: string | Date, now: Date = new Date()): string {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const base = `${d.getDate()} ${MONTHS[d.getMonth()]}`;
  return d.getFullYear() === now.getFullYear() ? base : `${base} ${d.getFullYear()}`;
}

/** Status pill text: "Paid 3 Oct", "Paid" (no date on record) or "Pending". */
export function statusLabel(r: WithdrawalRequest, now: Date = new Date()): string {
  if (!isPaid(r)) return "Pending";
  return r.payout?.paid_at ? `Paid ${shortDate(r.payout.paid_at, now)}` : "Paid";
}
